// navGroups — which sidebar groups are open, and where that is remembered.
//
// The desktop sidebar renders every destination a role can reach as an equally
// weighted 40px row. Measured in Chromium at 1440x900 on 2026-09-12, with the
// full-height sidebar of each role:
//
//   admin       26 rows   1495px of content in 844px   10 rows below the fold
//   manager     21 rows   1276px in 844px               6 rows below the fold
//   accounting  16 rows   1015px in 844px               1 row  below the fold
//   worker       8 rows    844px in 844px               fits
//
// So the thing an admin does twenty times a day and the thing they do twice a
// year were the same row, and ten of their rows could not be seen at all
// without scrolling a sticky column. This module is the state behind the fix:
// a group whose work is not this role's daily work starts collapsed to its
// heading, and its heading carries whatever is waiting inside it.
//
// ── Three inputs, in a fixed order of authority ─────────────────────────────
//
//   1. the DEFAULTS      navigation.js defaultOpenGroupsFor(role) — a prefix
//                        of that role's own GROUP_ORDER
//   2. what THIS PERSON  every group they opened or closed by hand, kept per
//      chose             device (localStorage), exactly like tabPrefs.js
//   3. where they ARE    the group holding the current screen is open, and
//                        nothing can close it
//
// (3) beats (2) beats (1), and (3) is the rule that makes the whole design
// safe: however a person has arranged their menu, navigating into a group
// shows them that group. A collapsed group can never be a place you are
// standing in and cannot see out of.
//
// ── Everything here is pure ─────────────────────────────────────────────────
//
// The storage object is passed in rather than reached for, so every rule is
// testable without a DOM. safeStorage() from tabPrefs.js wraps the real one —
// deliberately reused rather than re-implemented, because the reason it exists
// (private-mode Safari throws on the ACCESSOR, not just on setItem) is a fact
// about browsers and not about tabs.

export const STORAGE_KEY = 'fw.nav.groups.v1';

/**
 * The groups this person has opened or closed by hand.
 *
 * `{ Money: true, Admin: false }` — a label present means they chose; a label
 * absent means the default still applies. That is why this is a map and not a
 * list of open labels: "I closed Work" and "Work is not open by default" have
 * to be different, or reopening a group the defaults already open would be
 * impossible to express and a person's choice would be silently discarded on
 * the next release that changed OPEN_ROW_BUDGET.
 *
 * Anything unreadable — absent, not JSON, not a flat object of booleans —
 * reads as `null`, meaning "no preference". A corrupt value must never be able
 * to empty somebody's navigation, which is the same rule tabPrefs.js states
 * and for the same reason.
 */
export function readGroupPrefs(storage) {
  if (!storage) return null;
  let raw;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch (_) {
    return null;
  }
  if (!raw) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (_) {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const entries = Object.entries(parsed);
  if (!entries.every(([k, v]) => typeof k === 'string' && k && typeof v === 'boolean')) return null;
  return parsed;
}

/** Save the choices. Returns whether they actually persisted. */
export function writeGroupPrefs(storage, prefs) {
  if (!storage || !prefs || typeof prefs !== 'object' || Array.isArray(prefs)) return false;
  const clean = {};
  for (const [k, v] of Object.entries(prefs)) {
    if (typeof k === 'string' && k && typeof v === 'boolean') clean[k] = v;
  }
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(clean));
    return true;
  } catch (_) {
    return false;
  }
}

// ── There is deliberately NO reset (2026-09-13) ────────────────────────────
//
// A `clearGroupPrefs(storage)` lived here: exported, tested, and called by
// nothing. Review on #652 was right to flag it — an exported, tested function
// with no call site reads to the next person as "there is a reset somewhere",
// and there is not. The tab bar's `resetTabs` is inside AppShell's `phone`
// branch, and these preferences are desktop-only, so it could never have been
// reached from there.
//
// What that means, said plainly rather than left to be discovered: a person
// who closes a group has closed it for good on that browser, including
// against any future change to OPEN_ROW_BUDGET or to a role's defaults. That
// is what a preference IS, and it is the same deal `tabPrefs` offers — but
// tabPrefs has a way back and this does not.
//
// Deleted rather than wired, because the affordance belongs on a screen (a
// "reset my navigation" control a person can find), and inventing one from a
// sidebar change is how Tier C shell code grows surface nobody asked for.
// Re-adding the function is four lines when that screen exists.


/**
 * The set of group labels that are open right now.
 *
 * @param groups       navGroupsFor(role) — the groups actually being rendered
 * @param defaults     defaultOpenGroupsFor(role) — labels open on a fresh install
 * @param prefs        readGroupPrefs(...) — null when unset
 * @param activeGroup  the group holding the current screen, or null
 *
 * A label in `prefs` that no group carries is ignored rather than kept: a
 * stored choice outlives a role change and a renamed heading, and neither
 * should be able to reach into a menu it no longer describes.
 */
export function resolveOpenGroups(groups, defaults, prefs, activeGroup) {
  const known = new Set((groups || []).map((g) => g.label));
  const open = new Set((defaults || []).filter((label) => known.has(label)));
  if (prefs && typeof prefs === 'object') {
    for (const [label, wanted] of Object.entries(prefs)) {
      if (!known.has(label)) continue;
      if (wanted) open.add(label);
      else open.delete(label);
    }
  }
  // Where you are is always open, whatever anyone chose. This is last on
  // purpose — it is the rule nothing else may override.
  if (activeGroup && known.has(activeGroup)) open.add(activeGroup);
  return open;
}

/**
 * The prefs after toggling one group, ready to store.
 *
 * Always writes an explicit boolean rather than deleting the key on a return
 * to the default. "I want Admin closed" has to survive a future change to
 * OPEN_ROW_BUDGET that would otherwise reopen it, and a person who closes a
 * group and finds it open again next week has been overruled by a release.
 */
export function toggleGroup(prefs, label, isOpen) {
  const base = (prefs && typeof prefs === 'object' && !Array.isArray(prefs)) ? prefs : {};
  if (typeof label !== 'string' || !label) return base;
  return { ...base, [label]: !isOpen };
}

/**
 * What a collapsed heading has to say for itself: everything waiting inside.
 *
 * The same rule the phone's More tab already follows — AppShell sums the
 * badges of every destination behind More onto the More tab, because a
 * submitted timesheet nobody can see is the same as no submitted timesheet.
 * A collapsed group is exactly that situation at a desk.
 *
 * Returns 0 for an open group: its rows carry their own counts, and showing
 * the total twice would read as two different things waiting.
 */
export function groupBadge(group, badges, isOpen) {
  if (isOpen) return 0;
  return ((group && group.items) || []).reduce((n, i) => n + (Number((badges || {})[i.key]) || 0), 0);
}
